import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { formatUsdc, formatBps, parseUsdc, bnToNumber, pctToBps, bpsToPctStr } from '../../utils/format';
import { PACKAGE_NAMES, NUM_PACKAGES } from '../../utils/constants';
import { sendTx } from '../../utils/txHelpers';

export const PackageManager = () => {
  const { viewsR, adminW } = useContracts();
  const notify = useNotify();

  const [pkgs, setPkgs] = useState(null);
  const [selected, setSelected] = useState(0);
  const [price, setPrice] = useState('');
  const [roi, setRoi] = useState('');       // % now
  const [periods, setPeriods] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        const p = await viewsR.getAllPackages();
        if (cancelled) return;
        setPkgs(p);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, reload]);

  // Pre-fill inputs when selected package changes or data loads
  useEffect(() => {
    if (!pkgs) return;
    try {
      setPrice(formatUsdc(pkgs.prices[selected]).replace(/,/g, ''));
      setRoi(bpsToPctStr(pkgs.rois[selected]));
      setPeriods(String(bnToNumber(pkgs.periods[selected])));
    } catch (e) {}
  }, [pkgs, selected]);

  const update = async () => {
    if (!adminW) { notify.error('Wallet not ready'); return; }
    let priceBn;
    try { priceBn = parseUsdc(price); } catch (e) { notify.error('Invalid price'); return; }
    const roiBps = pctToBps(roi);
    if (roiBps == null) { notify.error('Invalid ROI %'); return; }
    if (roiBps === 0 || roiBps > 1000) { notify.error('Daily ROI must be > 0% and ≤ 10%'); return; }
    if (!periods || Number(periods) <= 0) { notify.error('Invalid duration'); return; }

    setBusy(true);
    try {
      await sendTx(adminW, 'updatePackage', [selected, priceBn, roiBps, periods], { notify, label: 'Update Package' });
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="card mb-24">
        <div className="section-title">All Packages</div>
        {!pkgs ? <div>Loading...</div> : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Price</th><th>Daily ROI</th><th>Duration</th><th>Max ROI</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: NUM_PACKAGES }).map((_, i) => {
                  const totalPct = (bnToNumber(pkgs.rois[i]) * bnToNumber(pkgs.periods[i])) / 100;
                  return (
                    <tr key={i}>
                      <td>{i}</td>
                      <td>{PACKAGE_NAMES[i]}</td>
                      <td>${formatUsdc(pkgs.prices[i])}</td>
                      <td>{formatBps(pkgs.rois[i])}</td>
                      <td>{bnToNumber(pkgs.periods[i])} days</td>
                      <td>{totalPct.toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="section-title">Update Package</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Package</label>
            <select className="select" value={selected} onChange={(e) => setSelected(Number(e.target.value))}>
              {Array.from({ length: NUM_PACKAGES }).map((_, i) => (
                <option key={i} value={i}>{i} — {PACKAGE_NAMES[i]}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Price (USDC)</label>
            <input className="input" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Daily ROI (%)</label>
            <input className="input" type="number" step="0.01" placeholder="0.25" value={roi} onChange={(e) => setRoi(e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>e.g. 0.25 for 0.25%/day · max 10%</div>
          </div>
          <div className="input-group">
            <label className="input-label">Duration (days)</label>
            <input className="input" type="number" value={periods} onChange={(e) => setPeriods(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={update} disabled={busy || !price || !roi || !periods}>
          {busy && <ButtonSpinner />}Update Package
        </button>
      </div>
    </div>
  );
};
